/**
 * Page-one notes "governor" (JIM-0919-03, Jim 2026-09-19).
 *
 * "The notes box knows how many lines page 1 has left (fewer stages = more
 * lines), shows a live counter and hard-stops there. We don't ever want to
 * go to the next page." The API measures the real page-one layout and says
 * how many lines fit and how long a line is; these helpers count lines the
 * same way the PDF wraps them and keep the text inside that box.
 */

export type NotesCapacity = {
  lines: number;
  chars_per_line: number;
  max_chars: number;
  line_height_pt: number;
  stage_count?: number;
};

/** Until the API answers: a typical three-stage page. */
export const FALLBACK_NOTES_CAPACITY: NotesCapacity = {
  lines: 12,
  chars_per_line: 130,
  max_chars: 1560,
  line_height_pt: 12,
};

/** Lines the note takes on paper: every newline starts a line, and a long
 * line wraps every `charsPerLine` characters. Empty text takes none. */
export function countNoteLines(text: string, charsPerLine: number): number {
  if (!text) return 0;
  const width = Math.max(charsPerLine, 1);
  return text
    .split('\n')
    .reduce((sum, seg) => sum + Math.max(1, Math.ceil(seg.length / width)), 0);
}

export function fitsNoteCapacity(text: string, cap: NotesCapacity): boolean {
  return countNoteLines(text, cap.chars_per_line) <= cap.lines;
}

/**
 * The longest prefix of `text` that still fits the box — what a hard stop
 * keeps when someone types or pastes past the end.
 */
export function clampNoteToCapacity(text: string, cap: NotesCapacity): string {
  if (fitsNoteCapacity(text, cap)) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (fitsNoteCapacity(text.slice(0, mid), cap)) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo);
}

/** "3 of 14 lines" for the counter under the box. */
export function governorLabel(text: string, cap: NotesCapacity): string {
  return `${countNoteLines(text, cap.chars_per_line)} of ${cap.lines} lines`;
}
